import { DestroyRef, inject, Injectable } from '@angular/core';
import { NgxIndexedDBService, WithID } from 'ngx-indexed-db';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import CryptoJS from 'crypto-js';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { IndexDBName } from '../global/constants';
import { IndexDbKeys } from '../../proxy/models/index-db-keys';

@Injectable({
    providedIn: 'root'
})
export class IndexDbService {
    private destroyRef = inject(DestroyRef);
    private secretKey: string = '8W3E6278CD5F1E59FB55218FFC3AF2DF2';

    constructor(private dbService: NgxIndexedDBService) {
    }

    public add(data: { key: IndexDbKeys, value: any, checkSumValue?: null | string }): Observable<{
        key: IndexDbKeys;
        value: string;
        expirationDate: number;
        checkSumValue: string | null
    } & WithID> {
        this.removeByKey(data.key);
        const newData = {
            key: data.key,
            value: this.encrypt(JSON.stringify(data.value)),
            expirationDate: new Date().getTime() + 24 * 60 * 60 * 1000,
            checkSumValue: data?.checkSumValue || null,
        }
        return this.dbService.add(IndexDBName, newData);
    }

    public getValueByKey<T>(key: IndexDbKeys): Observable<any> {
        return this.dbService.getByKey<T>(IndexDBName, key).pipe(
            map((data: any) => {
                if (data?.value && new Date().getTime() <= data.expirationDate) {
                    data.value = this.decrypt(data.value);
                    return JSON.parse(data.value);
                }
                return undefined;
            }),
        );
    }

    public getByKey<T>(key: IndexDbKeys): Observable<any> {
        return this.dbService.getByKey<T>(IndexDBName, key).pipe(
            map((data: any) => {
                if (data?.value && new Date().getTime() <= data.expirationDate) {
                    data.value = JSON.parse(this.decrypt(data.value));
                    return data;
                }
                return undefined;
            }),
            catchError((err) => {
                return of(false);
            }),
        );
    }

    public removeByKey(key: IndexDbKeys): void {
        this.dbService.deleteByKey(IndexDBName, key).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    private encrypt(txt: any): string {
        return CryptoJS.AES.encrypt(txt, this.secretKey).toString();
    }

    private decrypt(txtToDecrypt: any): string {
        return CryptoJS.AES.decrypt(txtToDecrypt, this.secretKey).toString(CryptoJS.enc.Utf8);
    }
}
